import { useCallback, useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { ApiService, RecalculatePublicCompsDependenciesRequest, RecalculatePublicCompsDependenciesResponse } from 'api';
import { ValuationsApproach } from 'common/types/valuation';
import { type RegistrationAPI } from 'pages/ValuationsAllocation/hooks/useApproachDependencyGraph';
import { type StableIdGenerator } from 'pages/ValuationsAllocation/hooks/useApproachStableIds';

const useDependenciesRecalculation = ({
  stableIds,
  dependencyGraph,
  approaches,
  valuationId,
}: {
  stableIds: StableIdGenerator;
  approaches: ValuationsApproach[];
  dependencyGraph: RegistrationAPI;
  valuationId?: number;
}) => {
  const [isRecalculating, setIsRecalculating] = useState(false);
  // a recalculation starts when this is set to a value (stable id from dependency graph). When finalizing the recalculation switch this to null
  const [sourceOfChange, setSourceOfChange] = useState<string | null>(null);
  const [recalculatedState, setRecalculatedState] = useState<RecalculatePublicCompsDependenciesResponse[] | null>(null);

  const makeRecalculateRequest = useCallback(async (statePayload: RecalculatePublicCompsDependenciesRequest) => {
    setIsRecalculating(true);
    try {
      enqueueSnackbar('Recalculating dependent approaches...', { variant: 'info' });
      const response = await ApiService.apiValuationPublicCompsRecalculateDependenciesCreate(statePayload);
      setRecalculatedState(response);
    } catch (error) {
      console.error('Error during recalculation:', error);
      setRecalculatedState(null);
    } finally {
      setIsRecalculating(false);
      setSourceOfChange(null);
    }
  }, []);

  useEffect(() => {
    const startRecalculation = async (
      publicCompsApproach: ValuationsApproach,
      dependencies: ValuationsApproach[],
      valuationId: number
    ) => {
      await makeRecalculateRequest({
        public_comps_state: publicCompsApproach.valuations_approach_gpc,
        approaches_dependencies: dependencies,
        calibrations_dependencies: [], // for now we only support recalculating dependencies related to public comps, so this will be empty
        valuation_id: valuationId,
      });
    };

    const dependentNodes = sourceOfChange ? dependencyGraph.getDependents(sourceOfChange) : [];
    if (valuationId && sourceOfChange && !isRecalculating && dependentNodes.length && approaches.length) {
      const dependenciesPanelIds = new Set(dependentNodes.map(node => node.panelId));
      const affectedApproaches = approaches.filter(approach => dependenciesPanelIds.has(approach.panelId!));
      const sourceOfTruthNode = dependencyGraph.readNode(sourceOfChange);
      const sourceOfTruthApproach = approaches.find(approach => approach.panelId === sourceOfTruthNode!.panelId);
      startRecalculation(sourceOfTruthApproach!, affectedApproaches, valuationId);
    }
  }, [sourceOfChange, dependencyGraph, approaches, makeRecalculateRequest, stableIds, valuationId, isRecalculating]);

  const applyRecalculationToApproaches = useCallback(
    (
      currentApproaches: ValuationsApproach[],
      recalculatedState: RecalculatePublicCompsDependenciesResponse[] | null
    ) => {
      if (!(recalculatedState ?? []).length) {
        return currentApproaches;
      }

      const updatesByPanelId = new Map<string, ValuationsApproach>();
      const updatesById = new Map<number, ValuationsApproach>();

      recalculatedState!
        .filter(({ status }) => status === 'success')
        .forEach(newState => {
          const responseApproach = newState?.approach as unknown as ValuationsApproach;

          const panelId = newState.panel_id;
          const normalizedApproach = {
            ...responseApproach,
            panelId: panelId ?? responseApproach.panelId,
          } as ValuationsApproach;

          if (panelId) {
            updatesByPanelId.set(panelId, normalizedApproach);
          }

          if (!isNil(normalizedApproach.id)) {
            updatesById.set(normalizedApproach.id, normalizedApproach);
          }
        });

      if (!updatesByPanelId.size && !updatesById.size) {
        return currentApproaches;
      }

      let didUpdate = false;
      const mergedApproaches = currentApproaches.map(approach => {
        const update =
          (approach.panelId && updatesByPanelId.get(approach.panelId)) ||
          (isNil(approach.id) ? undefined : updatesById.get(approach.id));

        if (!update) {
          return approach;
        }

        didUpdate = true;
        return {
          ...approach,
          ...update,
          panelId: approach.panelId ?? update.panelId,
        };
      });
      // clear recalculated state after applying updates
      // (efect depends on recalculatedState being truthy)
      setRecalculatedState(null);
      return didUpdate ? mergedApproaches : currentApproaches;
    },
    []
  );

  return {
    recalculatedState,
    isRecalculating,
    makeRecalculateRequest,
    setSourceOfChange,
    applyRecalculationToApproaches,
  };
};

export default useDependenciesRecalculation;
